import { API_BASE_URL } from '../lib/constants';
import { useAuthStore } from '../stores/authStore';

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
}

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

// Refresh the access token using the httpOnly refresh cookie
async function refreshAccessToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include', // Include httpOnly cookie
    });

    if (response.ok) {
      const data = await response.json();
      if (data.token) {
        if (data.user) {
          // Update Zustand store (also writes to localStorage)
          useAuthStore.getState().setAuth(data.user, data.token);
        } else {
          localStorage.setItem('token', data.token);
        }
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
}

// Wrapper to ensure only one refresh happens at a time
export async function tryRefreshToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = refreshAccessToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });

  return refreshPromise;
}

// Force logout and redirect to login
function forceLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('auth-storage');
  window.location.href = '/login';
}

async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  isRetry = false
): Promise<ApiResponse<T>> {
  const token = localStorage.getItem('token');

  const headers: HeadersInit = {
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  // Don't set Content-Type for FormData
  if (!(options.body instanceof FormData)) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for refresh token
    });

    // Handle 401 Unauthorized - try to refresh token
    if (response.status === 401 && !isRetry && !endpoint.includes('/auth/')) {
      const refreshed = await tryRefreshToken();
      if (refreshed) {
        // Retry original request with new token
        return apiRequest<T>(endpoint, options, true);
      } else {
        // Refresh failed, force logout
        forceLogout();
        return { error: 'Session expired. Please log in again.' };
      }
    }

    const data = await response.json();

    if (!response.ok) {
      // Handle structured validation errors with field-specific messages
      if (data.errors && Array.isArray(data.errors)) {
        const errorMessages = data.errors
          .map((err: { field?: string; message: string }) =>
            err.field ? `${err.field}: ${err.message}` : err.message
          )
          .join('\n');
        return { error: errorMessages || data.message || 'Validation error' };
      }
      return { error: data.message || 'An error occurred' };
    }

    return { data };
  } catch (error) {
    return { error: 'Network error. Please try again.' };
  }
}

export const api = {
  get: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    }),

  put: <T>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  delete: <T>(endpoint: string) => apiRequest<T>(endpoint, { method: 'DELETE' }),
};

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: User; token: string }>('/auth/login', { email, password }),

  register: (data: { email: string; password: string; full_name: string; contact_number?: string; state?: string; referral_code?: string }) =>
    api.post<{ user: User; token: string }>('/auth/register', data),

  forgotPassword: (email: string) =>
    api.post<{ message: string }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<{ message: string }>('/auth/reset-password', { token, password }),
};

// User API
export const userApi = {
  getProfile: () => api.get<User>('/users/me'),

  updateProfile: (data: { full_name?: string; contact_number?: string; touch_n_go?: string; state?: string }) =>
    api.put<User>('/users/me', data),

  submitLocalGuideLevel: (data: FormData) =>
    api.post<User>('/users/me/local-guide-level', data),

  getCredits: (page = 1, limit = 20, startDate?: string, endDate?: string) =>
    api.get<CreditResponse>(
      `/users/me/credits?page=${page}&limit=${limit}${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}`
    ),

  getMyTasks: (page = 1, limit = 20, status?: string, startDate?: string, endDate?: string) =>
    api.get<ParticipationsResponse>(
      `/users/me/tasks?page=${page}&limit=${limit}${status ? `&status=${status}` : ''}${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}`
    ),

  getGoogleAccounts: () =>
    api.get<{ accounts: GoogleAccount[] }>('/users/me/google-accounts'),

  addGoogleAccount: (data: { google_email: string; google_name?: string }) =>
    api.post<GoogleAccount>('/users/me/google-accounts', data),

  deleteGoogleAccount: (id: number) =>
    api.delete<{ message: string }>(`/users/me/google-accounts/${id}`),

  changePassword: (data: { current_password: string; new_password: string }) =>
    api.put<{ message: string }>('/users/me/password', data),

  getReferrals: () =>
    api.get<{ referrals: { id: number; full_name: string; joined_at: string; first_task_approved: boolean; credits_earned: number }[]; total: number; completed: number; total_credits_earned: number }>('/users/referrals'),
};

// Task API
export const taskApi = {
  list: (page = 1, limit = 20, filter = 'available', sort = 'newest') =>
    api.get<TasksResponse>(`/tasks?page=${page}&limit=${limit}&filter=${filter}&sort=${sort}`),

  get: (id: number) => api.get<TaskDetail>(`/tasks/${id}`),

  claim: (id: number, textOptionId?: number, googleAccountId?: number) =>
    api.post<{ message: string; participation: Participation; selected_text_option?: TextOption }>(
      `/tasks/${id}/claim`,
      {
        ...(textOptionId && { text_option_id: textOptionId }),
        ...(googleAccountId && { google_account_id: googleAccountId }),
      }
    ),

  submit: (id: number, data: FormData) =>
    api.post<{ message: string; participation: Participation }>(`/tasks/${id}/submit`, data),
};

// Admin API
export const adminApi = {
  getDashboardStats: () =>
    api.get<DashboardStats>('/admin/dashboard/stats'),

  getLocalGuideRequests: () =>
    api.get<{ requests: LocalGuideRequest[] }>('/admin/local-guide-requests'),

  approveLocalGuideRequest: (userId: number) =>
    api.post<{ message: string }>(`/admin/local-guide-requests/${userId}/approve`, {}),

  rejectLocalGuideRequest: (userId: number) =>
    api.post<{ message: string }>(`/admin/local-guide-requests/${userId}/reject`, {}),

  getTasks: (page = 1, limit = 20, status?: string, search?: string) =>
    api.get<AdminTasksResponse>(
      `/admin/tasks?page=${page}&limit=${limit}${status ? `&status=${status}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`
    ),

  getTask: (id: number) => api.get<Task & { participant_count: number }>(`/admin/tasks/${id}`),

  createTask: (data: CreateTaskData) => api.post<Task>('/admin/tasks', data),

  updateTask: (id: number, data: Partial<CreateTaskData>) =>
    api.put<Task>(`/admin/tasks/${id}`, data),

  deleteTask: (id: number) => api.delete<{ message: string }>(`/admin/tasks/${id}`),

  completeTask: (id: number) =>
    api.post<{ message: string; task: Task }>(`/admin/tasks/${id}/complete`),

  // Text options
  getTextOptions: (taskId: number) =>
    api.get<{ text_options: TextOption[] }>(`/admin/tasks/${taskId}/text-options`),

  addTextOption: (taskId: number, textContent: string, displayOrder?: number, imageUrls?: string[]) =>
    api.post<TextOption>(`/admin/tasks/${taskId}/text-options`, {
      text_content: textContent,
      display_order: displayOrder,
      image_urls: imageUrls || [],
    }),

  updateTextOption: (taskId: number, optionId: number, textContent: string, imageUrls?: string[]) =>
    api.put<TextOption>(`/admin/tasks/${taskId}/text-options/${optionId}`, {
      text_content: textContent,
      image_urls: imageUrls,
    }),

  notifyNewSlots: (taskId: number) =>
    api.post<{ message: string; notified_count: number }>(`/admin/tasks/${taskId}/notify-new-slots`),

  uploadTextOptionImage: async (taskId: number, file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('auth-storage');
    let authToken = '';
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token || '';
      } catch {
        // ignore parse error
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/tasks/${taskId}/text-options/upload-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      return response.json();
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw err;
    }
  },

  deleteTextOptionImage: async (taskId: number, imageUrl: string): Promise<{ message: string }> => {
    const token = localStorage.getItem('auth-storage');
    let authToken = '';
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token || '';
      } catch {
        // ignore parse error
      }
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/tasks/${taskId}/text-options/delete-image`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ image_url: imageUrl }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Delete failed');
      }

      return response.json();
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw err;
    }
  },

  deleteTextOption: (taskId: number, optionId: number) =>
    api.delete<{ message: string }>(`/admin/tasks/${taskId}/text-options/${optionId}`),

  reorderTextOptions: (taskId: number, optionIds: number[]) =>
    api.put<{ message: string }>(`/admin/tasks/${taskId}/text-options/reorder`, {
      option_ids: optionIds,
    }),

  toggleTextOptionClaim: (taskId: number, optionId: number, isClaimed: boolean) =>
    api.put<TextOption>(`/admin/tasks/${taskId}/text-options/${optionId}/toggle-claim`, {
      is_claimed: isClaimed,
    }),

  getReviews: (page = 1, limit = 20, status = 'pending', search = '', taskId?: number) =>
    api.get<ReviewsResponse>(
      `/admin/reviews?page=${page}&limit=${limit}&status=${status}${search ? `&search=${encodeURIComponent(search)}` : ''}${taskId ? `&task_id=${taskId}` : ''}`
    ),

  approveReview: (id: number) =>
    api.post<{ message: string }>(`/admin/reviews/${id}/approve`),

  rejectReview: (id: number, comment?: string) =>
    api.post<{ message: string }>(`/admin/reviews/${id}/reject`, { comment }),

  requestInfo: (id: number, comment: string) =>
    api.post<{ message: string }>(`/admin/reviews/${id}/request-info`, { comment }),

  allowResubmit: (id: number) =>
    api.post<{ message: string }>(`/admin/reviews/${id}/allow-resubmit`),

  cancelSubmission: (id: number) =>
    api.post<{ message: string }>(`/admin/reviews/${id}/cancel`),

  reverseApproval: (id: number) =>
    api.post<{ message: string; reversed_credits: number }>(`/admin/reviews/${id}/reverse-approval`),

  // Users
  getUsers: (page = 1, limit = 20, role?: string, search?: string) =>
    api.get<UsersResponse>(
      `/admin/users?page=${page}&limit=${limit}${role ? `&role=${role}` : ''}${search ? `&search=${encodeURIComponent(search)}` : ''}`
    ),

  getUsersWithCredits: (page = 1, limit = 20, status = 'with_balance', search = '') =>
    api.get<UsersWithCreditsResponse>(`/admin/users/with-credits?page=${page}&limit=${limit}&status=${status}${search ? `&search=${encodeURIComponent(search)}` : ''}`),

  getPerformance: (
    period: 'all' | 'month' | 'week' = 'all',
    sort: 'credits' | 'tasks' | 'approval_rate' = 'credits',
    page = 1,
    limit = 20,
  ) =>
    api.get<PerformanceResponse>(`/admin/performance?period=${period}&sort=${sort}&page=${page}&limit=${limit}`),

  getUser: (id: number) => api.get<UserDetail>(`/admin/users/${id}`),

  getUserTransactions: (userId: number, page = 1, limit = 20) =>
    api.get<UserTransactionsResponse>(
      `/admin/users/${userId}/transactions?page=${page}&limit=${limit}`
    ),

  getUserParticipations: (userId: number, page = 1, limit = 20) =>
    api.get<UserParticipationsResponse>(
      `/admin/users/${userId}/participations?page=${page}&limit=${limit}`
    ),

  processUserPayout: (userId: number, amount: number, remark?: string) =>
    api.post<{ message: string }>(`/admin/users/${userId}/payout`, { amount, remark }),

  adjustUserCredits: (userId: number, amount: number, note: string) =>
    api.post<{ message: string }>(`/admin/users/${userId}/adjust-credits`, { amount, note }),

  updateUser: (userId: number, data: {
    full_name?: string;
    contact_number?: string;
    touch_n_go?: string;
    remark?: string;
    role?: 'participant' | 'admin';
  }) => api.put<User>(`/admin/users/${userId}`, data),

  createUser: (data: {
    email: string;
    full_name: string;
    contact_number?: string;
    role?: 'participant' | 'admin';
    password?: string;
  }) => api.post<{ user: User; temporary_password: string; message: string }>('/admin/users', data),

  resetUserPassword: (userId: number, password?: string) =>
    api.post<{ message: string; temporary_password: string }>(`/admin/users/${userId}/reset-password`, password ? { password } : undefined),

  sendTestEmail: (email: string) =>
    api.post<{ message: string }>('/admin/settings/test-email', { email }),

  // Logs
  getLogs: () =>
    api.get<LogsListResponse>('/admin/logs'),

  getLogContent: (filename: string, lines = 500, offset = 0) =>
    api.get<LogContentResponse>(`/admin/logs/${encodeURIComponent(filename)}?lines=${lines}&offset=${offset}`),

  // Activity
  getActivity: (page = 1, limit = 50, activityType = 'all', startDate?: string, endDate?: string, userId?: number) =>
    api.get<ActivityResponse>(
      `/admin/activity?page=${page}&limit=${limit}${activityType !== 'all' ? `&activity_type=${activityType}` : ''}${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}${userId ? `&user_id=${userId}` : ''}`
    ),

  // Reports
  getTaskReport: (taskId: number) =>
    api.get<TaskReportResponse>(`/admin/reports/tasks/${taskId}`),

  downloadTaskReport: async (taskId: number) => {
    const token = localStorage.getItem('auth-storage');
    let authToken = '';
    if (token) {
      try {
        const parsed = JSON.parse(token);
        authToken = parsed.state?.token || '';
      } catch {
        // ignore parse error
      }
    }
    const response = await fetch(`${API_BASE_URL}/admin/reports/tasks/${taskId}/download`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    if (!response.ok) {
      throw new Error('Failed to download report');
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `merchant-report-${taskId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },

  getAdminReferrals: (page = 1, limit = 20) =>
    api.get<{
      stats: { total_referrers: number; total_referred: number; total_completed: number; total_credits_issued: number };
      referrals: { id: number; full_name: string; email: string; joined_at: string; has_completed: boolean; referrer_id: number; referrer_name: string; referrer_email: string; credits_issued: number }[];
      pagination: { page: number; limit: number; total: number; pages: number };
    }>(`/admin/referrals?page=${page}&limit=${limit}`),

  getSettings: () =>
    api.get<{ settings: { key: string; value: string; description: string; updated_at: string }[] }>('/admin/settings'),

  updateSetting: (key: string, value: string) =>
    api.put<{ message: string; key: string; value: string }>(`/admin/settings/${key}`, { value }),
};

// Announcement API
export const announcementApi = {
  list: () =>
    api.get<{ announcements: Announcement[] }>('/announcements'),

  get: (id: number) =>
    api.get<AnnouncementDetail>(`/announcements/${id}`),

  adminList: () =>
    api.get<{ announcements: AnnouncementAdmin[] }>('/announcements/admin'),

  adminGet: (id: number) =>
    api.get<AnnouncementFull>(`/announcements/admin/${id}`),

  create: (data: { title: string; content: string; is_active?: boolean }) =>
    api.post<AnnouncementFull>('/announcements/admin', data),

  update: (id: number, data: { title?: string; content?: string; is_active?: boolean }) =>
    api.put<AnnouncementFull>(`/announcements/admin/${id}`, data),

  delete: (id: number) =>
    api.delete<{ message: string }>(`/announcements/admin/${id}`),
};

// Guide API
export const guideApi = {
  get: () => api.get<{ content: string; updated_at: string }>('/guide'),

  update: (content: string) =>
    api.put<{ content: string; updated_at: string }>('/guide', { content }),

  // Guide Sections (public)
  getSections: (search?: string, tags?: string[]) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (tags && tags.length > 0) params.append('tags', tags.join(','));
    const queryString = params.toString();
    return api.get<{ sections: GuideSection[]; availableTags: string[] }>(
      `/guide/sections${queryString ? `?${queryString}` : ''}`
    );
  },

  getSection: (id: number) =>
    api.get<GuideSectionDetail>(`/guide/sections/${id}`),

  // Guide Sections (admin)
  getAdminSections: () =>
    api.get<{ sections: GuideSectionAdmin[] }>('/guide/admin/sections'),

  getAdminSection: (id: number) =>
    api.get<GuideSectionFull>(`/guide/admin/sections/${id}`),

  createSection: (data: { title: string; content: string; is_active?: boolean; tags?: string[] }) =>
    api.post<GuideSectionFull>('/guide/admin/sections', data),

  updateSection: (id: number, data: { title?: string; content?: string; is_active?: boolean; tags?: string[] }) =>
    api.put<GuideSectionFull>(`/guide/admin/sections/${id}`, data),

  deleteSection: (id: number) =>
    api.delete<{ message: string }>(`/guide/admin/sections/${id}`),

  reorderSections: (sectionIds: number[]) =>
    api.put<{ message: string }>('/guide/admin/sections/reorder', { section_ids: sectionIds }),

  uploadImage: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append('image', file);

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_BASE_URL}/guide/admin/upload-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Upload failed');
      }

      return response.json();
    } catch (err) {
      if (err instanceof TypeError) {
        throw new Error('Network error. Please check your connection and try again.');
      }
      throw err;
    }
  },
};

// Types
export interface User {
  id: number;
  email: string;
  full_name: string;
  contact_number?: string;
  role: 'participant' | 'admin';
  credit_balance: number;
  touch_n_go?: string;
  remark?: string;
  referral_code?: string;
  created_at: string;
  state?: string;
  local_guide_level?: number;
  pending_local_guide_level?: number | null;
  pending_local_guide_screenshot?: string | null;
  pending_local_guide_requested_at?: string | null;
}

export interface LocalGuideRequest {
  id: number;
  full_name: string;
  email: string;
  local_guide_level: number;
  pending_local_guide_level: number;
  pending_local_guide_screenshot: string;
  pending_local_guide_requested_at: string;
}

export interface TextOption {
  id: number;
  task_id: number;
  text_content: string;
  image_urls: string[];
  is_claimed: boolean;
  claimed_by?: number;
  claimed_by_name?: string;
  claimed_by_email?: string;
  display_order: number;
  created_at: string;
}

export interface Task {
  id: number;
  name: string;
  target_url: string;
  max_participants: number;
  credit_reward: number;
  start_date: string;
  end_date: string;
  customer_name?: string;
  customer_contact?: string;
  instructions?: string;
  status: 'draft' | 'active' | 'completed' | 'expired';
  payment_received: boolean;
  payment_completed: boolean;
  text_options_required: boolean;
  participant_count: number;
  approved_count?: number;
  pending_count?: number;
  created_at: string;
  user_participation_status?: string;
  review_type?: 'facebook_like' | 'google_review';
  claim_timeout_hours?: number;
  guide_section_id?: number;
  merchant_payment_amount?: number;
  merchant_payment_date?: string;
  merchant_payment_notes?: string;
}

export interface TaskDetail extends Task {
  participants: {
    full_name: string;
    status: string;
    submitted_at?: string;
    selected_text_option_id?: number;
    google_email?: string;
    google_name?: string;
  }[];
  text_options: TextOption[];
  user_participation?: Participation & {
    selected_text_option?: TextOption;
  };
  user_participations?: (Participation & {
    selected_text_option?: TextOption;
  })[];
  claim_cooldown?: {
    blocked: boolean;
    reason: 'active_claim' | 'cooldown' | 'all_accounts_busy' | null;
    message?: string;
    cooldown_ends_at?: string;
    available_accounts?: { id: number; google_email: string; google_name?: string }[];
    blocked_accounts?: { id: number; google_email: string; google_name?: string; reason: string; cooldown_ends_at?: string }[];
  };
}

export interface Participation {
  id: number;
  user_id: number;
  task_id: number;
  status: 'claimed' | 'submitted' | 'approved' | 'rejected';
  submission_text?: string;
  submission_file?: string;
  submitted_at?: string;
  reviewed_at?: string;
  reviewed_by?: number;
  review_comment?: string;
  selected_text_option_id?: number;
  google_account_id?: number;
  google_email?: string;
  google_name?: string;
  created_at: string;
}

export interface CreditTransaction {
  id: number;
  amount: number;
  type: 'reward' | 'payment' | 'adjustment';
  description: string;
  task_name?: string;
  created_at: string;
}

export interface GoogleAccount {
  id: number;
  user_id: number;
  google_email: string;
  google_name?: string;
  created_at: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface TasksResponse {
  tasks: Task[];
  pagination: Pagination & {
    available_for_user?: number;
  };
}

export interface AdminTasksResponse {
  tasks: (Task & { pending_count: number })[];
  pagination: Pagination;
}

export interface ParticipationsResponse {
  participations: (Participation & {
    task_id: number;
    task_name: string;
    target_url: string;
    credit_reward: number;
    end_date: string;
    task_status: string;
    selected_text_content?: string;
    review_type?: 'facebook_like' | 'google_review';
    google_email?: string;
    google_name?: string;
  })[];
  pagination: Pagination;
}

export interface CreditResponse {
  balance: number;
  transactions: CreditTransaction[];
  pagination: Pagination;
}

export interface ReviewSubmission extends Participation {
  full_name: string;
  email: string;
  task_name: string;
  credit_reward: number;
  claim_timeout_hours?: number | null;
  claimed_at?: string;
  reviewer_name?: string;
  google_accounts?: {
    google_email: string;
    google_name?: string;
  }[];
  google_account_used?: {
    google_email: string;
    google_name?: string;
  } | null;
  selected_text_option?: {
    id: number;
    text_content: string;
    image_urls?: string[];
  } | null;
}

export interface ReviewsResponse {
  submissions: ReviewSubmission[];
  pagination: Pagination;
}

export interface UsersResponse {
  users: User[];
  pagination: Pagination;
}

export interface CreateTaskData {
  name: string;
  target_url: string;
  max_participants: number;
  credit_reward: number;
  start_date: string;
  end_date: string;
  customer_name?: string;
  customer_contact?: string;
  instructions?: string;
  status?: 'draft' | 'active' | 'inactive';
  text_options_required?: boolean;
  text_options?: { text_content: string }[];
  review_type?: 'facebook_like' | 'google_review';
  claim_timeout_hours?: number | null;
  guide_section_id?: number | null;
  merchant_payment_amount?: number;
  merchant_payment_date?: string;
  merchant_payment_notes?: string;
}

export interface UserWithCredits extends User {
  total_earned: number;
  total_paid: number;
}

export interface UsersWithCreditsResponse {
  users: UserWithCredits[];
  pagination: Pagination;
}

export interface UserPerformance {
  id: number;
  full_name: string;
  email: string;
  created_at: string;
  credit_balance: number;
  total_earned: number;
  total_paid: number;
  tasks_approved: number;
  tasks_reviewed: number;
  tasks_in_progress: number;
  approval_rate: number | null;
  last_active_at: string | null;
}

export interface PerformanceResponse {
  performers: UserPerformance[];
  pagination: Pagination;
  period: 'all' | 'month' | 'week';
}

export interface UserDetail extends User {
  total_earned: number;
  total_paid: number;
  google_accounts?: GoogleAccount[];
}

export interface UserTransaction {
  id: number;
  user_id: number;
  amount: number;
  type: 'reward' | 'payment' | 'adjustment';
  description: string;
  task_id?: number;
  task_name?: string;
  created_by?: number;
  created_by_name?: string;
  created_at: string;
}

export interface UserTransactionsResponse {
  transactions: UserTransaction[];
  pagination: Pagination;
}

export interface UserParticipation extends Participation {
  task_name: string;
  credit_reward: number;
  target_url: string;
}

export interface UserParticipationsResponse {
  participations: UserParticipation[];
  pagination: Pagination;
}

// Announcement Types
export interface Announcement {
  id: number;
  title: string;
  preview: string;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementDetail {
  id: number;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementAdmin {
  id: number;
  title: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AnnouncementFull {
  id: number;
  title: string;
  content: string;
  is_active: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
}

// Guide Section Types
export interface GuideSection {
  id: number;
  title: string;
  content: string;
  tags: string[];
  display_order: number;
  updated_at: string;
}

export interface GuideSectionDetail {
  id: number;
  title: string;
  content: string;
  tags: string[];
  updated_at: string;
}

export interface GuideSectionAdmin {
  id: number;
  title: string;
  is_active: boolean;
  tags: string[];
  display_order: number;
  updated_at: string;
}

export interface GuideSectionFull {
  id: number;
  title: string;
  content: string;
  is_active: boolean;
  tags: string[];
  display_order: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
}

export interface DashboardActivityEvent {
  id: number;
  event_type: 'claimed' | 'submitted' | 'approved' | 'rejected';
  user_name: string;
  task_name: string;
  event_date: string;
  google_email?: string;
}

export interface DashboardStats {
  activeTasks: number;
  pendingReviews: number;
  totalUsers: number;
  totalTasks: number;
  pendingUserAction: number;
  openSlots: number;
  awaitingPayment: number;
  totalCredits: number;
  recentActivity: DashboardActivityEvent[];
}

// Logs Types
export interface LogFile {
  name: string;
  size: number;
  modified_at: string;
}

export interface LogsListResponse {
  files: LogFile[];
}

export interface LogContentResponse {
  filename: string;
  content: string;
  total_lines: number;
  offset: number;
  lines_returned: number;
  has_more: boolean;
}

// Activity Types
export interface ActivityEvent {
  id: number;
  activity_type: string;
  user_id: number;
  user_name: string;
  user_email: string;
  task_id: number | null;
  task_name: string | null;
  performed_by: number | null;
  performed_by_name: string | null;
  amount: number | null;
  details: Record<string, unknown> | null;
  ip_address: string | null;
  event_date: string;
}

export interface ActivityResponse {
  events: ActivityEvent[];
  pagination: Pagination;
}

// Report Types
export interface ReportSubmission {
  id: number;
  user_id: number;
  full_name: string;
  email: string;
  google_email?: string;
  google_name?: string;
  submission_file: string | null;
  submission_text: string | null;
  submitted_at: string;
  reviewed_at: string;
}

export interface TaskReportResponse {
  task: {
    id: number;
    name: string;
    credit_reward: number;
    start_date: string;
    end_date: string;
  };
  stats: {
    total_participants: number;
    total_credits: number;
  };
  submissions: ReportSubmission[];
}
